import {VanillaReader} from "../VanillaReader/VanillaReader";
import {
    IVanillaReaderContentPositionData,
    IVanillaReaderDocumentLandmark,
    IVanillaReaderHighlightData,
    IVanillaReaderNavigationAction,
    IVanillaReaderOptionsData,
    IVanillaReaderPublicationLandmarkCollection,
    IVanillaReaderPublicationStyleOptions, IVanillaReaderPublicationStylePalette,
    IVanillaReaderShelfItemData,
    IVanillaReaderSyncMediaOptions,
    IVanillaReaderTransformData,
    IVanillaReaderUiColorTheme,
    IVanillaReaderViewOptions,
    VanillaReaderPublicationLandmarkType,
    VanillaReaderRendererNames,
} from "../VanillaReader/VanillaReaderModel";

import {ILocator, ILocatorData, ISimpleLocatorData} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import {
    IReaderViewAnnotationLayer,
    IReaderViewGotoOptions,
    ISyncMediaPlaybackRangeOptions,
    ISyncMediaTimelineRangeData,
    ITransformRectZoomOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {
    IEpubReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import {
    IPdfReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-pdf";
import {
    IWpAudiobookReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-wp-audiobook";
import {LengthUnit} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {IVanillaReaderAppEventTypeMap} from "../VanillaReaderEventBus/IVanillaReaderAppEventTypeMap";
import {CustomEventListener, VanillaReaderEventBus} from "../VanillaReaderEventBus/VanillaReaderEventBus";
import {ISpeechSynthesisVoiceCollection} from "../utils/WebSpeechUtils";
import {VanillaReaderAppMain} from "../VanillaReaderApp/VanillaReaderAppMain";
import {IVanillaReaderUiOptionsData} from "../VanillaReaderUI/VanillaReaderUiModel";
import {VanillaReaderAppAssets} from "../VanillaReaderApp/VanillaReaderAppAssets";
import {VanillaReaderAppUiDefaults} from "../VanillaReaderUI/VanillaReaderAppUiDefaults";
import { IFragmentSelector, SelectorType } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-base";

/*
* # VanillaReaderAPI
*
*
* ## RESPONSIBILITIES
*
* The VanillaReaderAPI acts as a "glue" between the UI and the application logic. It defines the public API for the
* Vanilla Reader. This level of abstraction makes the architecture more robust, since the UI will not be affected by
* changes made in the underlying application.
*
* If you find that need to expose new functionality, or get additional data from the `VanillaReader` module, do not
* fall for the temptation to access the VanillaReader class directly. Instead, implement a new method for the use case
* in this class. This is the way. I have spoken.
*
* ## METHOD CALLS YIELD EVENTS
*
* In almost all cases calling the methods in this API will return void. This is because most of the methods have a
* range of asynchronous side effects in the `VanillaReader`. Also, this strategy means that you can call the API
* methods in more than location in the UI code (not that I would necessarily recommend this) and handle the state
* changes in a centralized location.
*
* There are exceptions to this however where the method has no side effects and simply return a value, such as
* `getTimelinePosition`.
*
* ## WHAT HAPPENED TO THE INTENT EVENTS?
*
* A note if you have used the previous Vanilla Reader code base. This API is equivalent to the event based API. The
* reason that I have "ported" it over to use method calls is to make the UI code easier to understand at a glance.
* You will find a method for each of the intents, and the parameters are the same as the event detail you previously
* used.
*
* ## VANILLA READER TYPES USED
*
* - VanillaReaderAppMain
* - VanillaReader
*
* */

export interface IVanillaReaderEventData<T extends keyof IVanillaReaderAppEventTypeMap> extends CustomEvent {
    type: string,
    detail: IVanillaReaderAppEventTypeMap[T],
}

export type VanillaReaderEventCallback<T extends keyof IVanillaReaderAppEventTypeMap> = (eventData: IVanillaReaderEventData<T>) => void;

export class VanillaReaderAPI {
    private static _vanillaReaderApiInstance: VanillaReaderAPI | undefined;
    private _vanillaReader: VanillaReader | undefined;

    private constructor(private _vanillaReaderAppMain: VanillaReaderAppMain) {
        this._vanillaReaderAppMain.onVanillaReaderInstanceCreated(()=>{
            const vanillaReader = this._vanillaReaderAppMain.getVanillaReaderInstance();
            if(!vanillaReader) {
                throw new Error('VanillaReaderAPI.constructor(): vanillaReaderAppMain.getVanillaReaderInstance() returned undefined.');
            }

            this._vanillaReader = vanillaReader;

            VanillaReaderEventBus.addEventListener('VIEW_OPTIONS_CHANGED', (_ev): void => {
                const uiOptionsData = this.serializeToVanillaReaderAppUiOptionsData();
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('APP_UI_OPTIONS_CHANGED', {uiOptionsData}));
            });

        });
    }

    private get vanillaReader(): VanillaReader {
        if(!this._vanillaReader) {
            throw new Error('VanillaReaderAPI.vanillaReader(): VanillaReader has not been instantiated.')
        }
        return this._vanillaReader;
    }

    public static get instance(): VanillaReaderAPI | undefined {
        return VanillaReaderAPI._vanillaReaderApiInstance;
    }

    public static create(appMain: VanillaReaderAppMain): VanillaReaderAPI {
        VanillaReaderAPI._vanillaReaderApiInstance = new VanillaReaderAPI(appMain);
        return VanillaReaderAPI._vanillaReaderApiInstance!;
    }

    public get shouldFocusOnReadingPosition(): boolean {
        return this.vanillaReader.shouldFocusOnReadingPosition;
    }

    public get isOffline(): boolean {
        return this.vanillaReader.isOffline;
    }

    public get isMobileScreenSize(): boolean {
        return window.matchMedia('only screen and (max-width: 480px)').matches;
    }

    public get isInDarkMode(): boolean {
        return this._vanillaReaderAppMain.activeAppUiTheme?.uiIsDark || true;
    }

    public get useDeviceStorage(): boolean {
        return this._vanillaReaderAppMain.useDeviceStorage;
    }

    setAppMainColorTheme(colorTheme: IVanillaReaderUiColorTheme): void {
        this._vanillaReaderAppMain.changeAppUiColorTheme(colorTheme);
    }

    static addEventListener<T extends keyof IVanillaReaderAppEventTypeMap>(
        event: T, listener: CustomEventListener<IVanillaReaderAppEventTypeMap[T]>, once: boolean = false,
    ): void {
        VanillaReaderEventBus.addEventListener<T>(event, listener, once);
    }

    static removeEventListener<T extends keyof IVanillaReaderAppEventTypeMap>(
        eventType: T, listener: CustomEventListener<IVanillaReaderAppEventTypeMap[T]>,
    ): void {
        VanillaReaderEventBus.removeEventListener<T>(eventType, listener);
    }

    static dispatchEvent<T extends keyof IVanillaReaderAppEventTypeMap>(event: CustomEvent<IVanillaReaderAppEventTypeMap[T]>): void {
        VanillaReaderEventBus.dispatchEvent<T>(event);
    }

    serializeToVanillaReaderOptionsData(): IVanillaReaderOptionsData {
        return this.vanillaReader.serializeToOptionsData();
    }

    serializeToVanillaReaderAppUiOptionsData(): IVanillaReaderUiOptionsData {
        return this._collateVanillaReaderUiOptionsData();
    }

    /**
     *
     * This method takes in an AWS UID and tries to load a publication from it. "Under the hood" it uses the
     * Colibrio `ReadingSystemEngine`, so have a peek at the `VanillaReaderReadingSystem.loadPublicationFromAws()`
     *
     * @param uid - Base64 Encoded UID
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @return void
     *
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOADED
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOAD_FAILED
     * */
    loadPublicationFromUid(
        uid: string,
        initialLocator: string | undefined,
    ): void {
        this._vanillaReaderAppMain.loadPublicationFromAws(uid, initialLocator).catch(console.warn);
    }

    /**
     *
     * This method takes in a File object and tries to load a publication from it. "Under the hood" it uses the
     * Colibrio `ReadingSystemEngine`, so have a peek at the `VanillaReaderReadingSystem.loadPublicationFromFile()`
     *
     * @param fileObject - A File object to the publication resource.
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @param storeOnDevice - Optional. Should the loaded publication data be cached on the device for offline access?.
     * @return void
     *
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOADED
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOAD_FAILED
     * */

    loadPublicationFromFile(
        fileObject: File,
        initialLocator: string | undefined,
        storeOnDevice: boolean,
    ): void {
        this._vanillaReaderAppMain.loadPublicationFromFile(fileObject, initialLocator, storeOnDevice).catch(console.warn);
    }

    /**
     *
     * This method takes in a URL as a string and tries to load a publication from it. "Under the hood" it uses the
     * Colibrio `ReadingSystemEngine`, so have a peek at the `VanillaReaderReadingSystem.loadPublicationFromUrl()`
     *
     * @param url - A string URL that points to a publication resource file
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @param storeOnDevice - Optional. Should the `IRandomAccessDataSource` instance used when loading the publication use IndexedDB for long term caching?
     * @return void
     *
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOADED
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOAD_FAILED
     * */
    loadPublicationFromUrl(
        url: string,
        initialLocator: string | undefined,
        storeOnDevice: boolean,
    ): void {
        this._vanillaReaderAppMain.loadPublicationFromUrl(url, initialLocator, storeOnDevice).catch(console.warn);
    }

    /**
     *
     * This method takes in a IVanillaReaderShelfItemData object. It identifies the origin of the file and tries to load
     * the publication data from using the `IVanillaReaderStreamedResourceStorage` storage client.
     *
     * @param shelfItemData - "Shelf item data" contains metadata about the "used book", along with reading position, bookmarks and hightlights.
     * @return void
     *
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOADED
     * @fires IVanillaReaderAppEvent_PUBLICATION_LOAD_FAILED
     * */
    loadPublicationFromShelfItemData(shelfItemData: IVanillaReaderShelfItemData): void {
        this._vanillaReaderAppMain.loadPublicationFromShelfItemData(shelfItemData).catch(console.warn);
    }

    closePublication(): void {
        VanillaReaderEventBus.dispatchEvent(new CustomEvent('PUBLICATION_CLOSE_INTENT'));
        this._vanillaReaderAppMain.closePublication();
    }

    /**
     *
     * Navigates to the position described in the locator.
     *
     * @param locator - The locator to navigate to as an `ILocator` or as a string
     * @param options - The `IReaderViewGotoOptions.setReadingPositionToVisibleRangeStart` option "snaps" the reading
     * position to the start of the visible content if set to `true`. Default is `false` meaning that the reading position
     * will be at the precise position described in the locator.
     *
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     *
     * */
    goTo(locator: ILocator | string, options?: IReaderViewGotoOptions): void {
      console.log(`GoTo: ${locator.toString()}`);
        this.vanillaReader.goTo(
            locator,
            options
        ).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     * */
    goToStart(): void {
        this.vanillaReader.goToStart().catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     * */
    next(): void {
        this.vanillaReader.next().catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     * */
    previous(): void {
        this.vanillaReader.previous().catch(console.warn);
    }

    /**
     *
     * This method navigates to a location in the book based on a given position off the readingProgressionTimeline.
     * The behavior differs a bit depending on the book format. If it's an EPUB or PDF we first need to resolve the
     * timeline position to a `IContentLocation` that we can then use as a parameter when calling `goTo`.
     * If it is an Audiobook things are much simpler. Since the timeline represents the duration of the audiobook in
     * milliseconds, we can just seek directly to that position in the book.
     *
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     *
     * * */
    gotoTimelinePosition(position: number): void {
        this.vanillaReader.gotoTimelinePosition(position).catch(console.warn);
    }

    /**
     *
     * This method gets the timeline position as a number. If the loaded `IReaderPublication` is an Audiobook the number
     * represents the progression in ms. If the `IReaderPublication` is a reflowable EPUB the returned number represents
     * the index of the word at the current position. For PDF and Fixed Layout EPUBs the number represents the current
     * page index.
     *
     * */
    getTimelinePosition(): Promise<number | undefined> {
        return this.vanillaReader.getTimelinePosition();
    }

    createAnnotationLayer(
        locator: string | ISimpleLocatorData | ILocator | null
    ): void {
        if (typeof locator !== 'undefined' && this._vanillaReader?.vanillaPublication && this.vanillaReader.vanillaReaderView){
            
            locator = this._vanillaReader?.vanillaPublication.colibrioReaderPublication.getDefaultLocatorUrl() + "#" + locator;
            let locatorParsed = this._vanillaReader?.vanillaPublication.colibrioReaderPublication.getLocatorFactory().parse(locator!);

            if (locatorParsed.locator && 
                locatorParsed.locator.getSelectors().length > 0 && 
                locatorParsed.locator.getSelectors()[0].isRangeSelector())
            {
                let annotationLayer: IReaderViewAnnotationLayer = this.vanillaReader.vanillaReaderView.colibrioReaderView.createAnnotationLayer("share-selection-highlight");
                annotationLayer.setLayerOptions({
                    layerStyle: {
                        'mix-blend-mode': 'multiply',
                        'opacity': '0.4',
                    },
                });
                annotationLayer.destroyAllAnnotations();
                let annotation = annotationLayer.createAnnotation(locatorParsed.locator);
                annotation.setOptions({
                    rangeStyle: {
                        'background-color': '#36d677',
                    },
                });
            }
        }
    }

    setRenderableDocuments(
        spreadAccess: string | null
    ): void {
        let readerPublication = this._vanillaReader?.vanillaPublication?.colibrioReaderPublication;
        if (typeof readerPublication === 'undefined') return;

        let readerDocuments = readerPublication.getSpine().filter((readerDocument) => {
            return readerDocument.getSourceContentDocument().isInLinearContent();
        });

        if (spreadAccess){
            if (spreadAccess !== "\"all\"" && JSON.parse(spreadAccess)){
                console.log("Colibrio access is restricted (A)");
                let accessArray = JSON.parse(spreadAccess);
                readerDocuments = readerPublication.getSpine().filter((readerDocument) => {
                    var isAllowed = false;
                    let selector = readerDocument.getLocator().getSelectors()[0];
                    if (selector.getType() == SelectorType.FRAGMENT_SELECTOR){
                        let cfi = selector as IFragmentSelector;

                        if (typeof accessArray[cfi.getValue()] !== 'undefined'){
                            isAllowed = accessArray[cfi.getValue()];          
                        } else if (typeof accessArray["#" + cfi.getValue()] !== 'undefined'){
                            isAllowed = accessArray["#" + cfi.getValue()];          
                        } else if (typeof accessArray[readerPublication?.getDefaultLocatorUrl() + "#" + cfi.getValue()] !== 'undefined'){
                            isAllowed = accessArray[readerPublication?.getDefaultLocatorUrl() + "#" + cfi.getValue()];          
                        }
                    }
                    return readerDocument.getSourceContentDocument().isInLinearContent() && isAllowed;
                });
            } else if (spreadAccess !== "\"all\"" && spreadAccess !== ""){
                console.log("Colibrio access is restricted (S)");
                let accessArray = spreadAccess.split(",").map(val => {
                    if (isNaN(parseInt(val))){
                        return val;
                    }
                    return parseInt(val) - 1;
                });

                if (accessArray.length > 0){
                    readerDocuments = readerPublication.getSpine().filter((readerDocument) => {
                        let isAllowed = false;
                        let selector = readerDocument.getLocator().getSelectors()[0];
                        if (selector.getType() == SelectorType.FRAGMENT_SELECTOR){
                            let cfi = selector as IFragmentSelector;
                            if (accessArray.indexOf(cfi.getValue()) > -1){
                                isAllowed = true;
                            }
                        }
                        return readerDocument.getSourceContentDocument().isInLinearContent() && isAllowed;
                    });
                } 
            } 
        }
        this._vanillaReader?.vanillaReaderView?.setReaderDocuments(readerDocuments);
    }

    /**
     *
     * This method searches the entire publication, or a subset of documents, for the provided search string.
     * In order to be able to perform a search, the search index must first be built. This is done adhoc in the
     * `VanillaReaderPublication.search()` method.
     *
     * @fires IVanillaReaderAppEvent_PUBLICATION_SEARCH_INDEX_BUILD_PROGRESS
     * @fires IVanillaReaderAppEvent_PUBLICATION_SEARCH_INDEX_READY
     * @fires IVanillaReaderAppEvent_PUBLICATION_SEARCH_COMPLETED
     *
     * */
    searchPublication(
        searchString: string,
        resultSetMaxSize: number,
        documentIndexes?: number[],
        highlightResults: boolean = true,
    ): void {
        this.vanillaReader.searchPublication(
            searchString,
            resultSetMaxSize,
            documentIndexes,
            highlightResults
        ).then((resultSet)=>{
            const event = VanillaReaderEventBus.createEvent('PUBLICATION_SEARCH_COMPLETED', {resultSet});
            VanillaReaderEventBus.dispatchEvent(event);
        }).catch(console.warn);

    }

    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_SEARCH_CLEARED
     * */
    clearPublicationSearchLayer(): void {
        return this.vanillaReader.clearPublicationSearchLayer();
    }

    /**
     * This method is a "quirkfix" to let Screen Reader users (primarily NVDA and JAWS) highlight text based on the contents
     * of their clipboard.
     *
     * The reason that we need this is because of the Screen Readers inability to scroll the document as it moves through
     * the document contents. This is a problem for us since we use the visible range as a means to know where the user
     * is reading, so without a change in the visible range we can't can add a correct highlight or bookmark.
     *
     * The way that the Screen Reader user will use this feature is as follows:
     *
     * 1. The user selects and copies the text that represents the location they want highlight or bookmark.
     * 2. The user uses some method to trigger a UI call to this method, sending along the text that is currently
     * in the user's clipboard as well as the document index in the publication spine.
     * 3. This method uses the publication search to grab the first occurrence of the string in the specified document.
     * 4. The result is communicated back to the user using the `HIGHLIGHT_ADDED` / `BOOKMARK_ADDED` or
     * `HIGHLIGHT_TEXTLOCATION_FAILED` / `BOOKMARK_TEXTLOCATION_FAILED` events.
     *
     * @fires IVanillaReaderAppEvent_BOOKMARK_ADDED
     * @fires IVanillaReaderAppEvent_BOOKMARK_TEXTLOCATION_FAILED
     *
     * */

    bookmarkTextLocation(
        textToBookmark: string,
        documentIndex: number,
    ): void {
        this.vanillaReader.bookmarkTextLocation(
            textToBookmark,
            documentIndex
        ).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_BOOKMARK_ADDED
     * */
    bookmarkReadingPosition(): void {
        this.vanillaReader.bookmarkReadingPosition().catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_HIGHLIGHT_ADDED
     * */
    addHighlight(highlightData: IVanillaReaderHighlightData): void {
        this.vanillaReader.addHighlight(highlightData).catch(console.warn);
    }

    /**
     * This method is a quirkfix to let Screen Reader users (primarily NVDA and JAWS) highlight text based on the contents
     * of their clipboard.
     *
     * The reason that we need this is because of the Screen Readers inability to scroll the document as it moves through
     * the document contents. This is a problem for us since we use the visible range as a means to know where the user
     * is reading, so without a change in the visible range we can't can add a correct highlight or bookmark.
     *
     * The way that the Screen Reader user will use this feature is as follows:
     *
     * 1. The user selects and copies the text that represents the location they want highlight or bookmark.
     * 2. The user uses some method to trigger a UI call to this method, sending along the text that is currently
     * in the user's clipboard as well as the document index in the publication spine.
     * 3. This method uses the publication search to grab the first occurrence of the string in the specified document.
     * 4. The result is communicated back to the user using the `HIGHLIGHT_ADDED` / `BOOKMARK_ADDED` or
     * `HIGHLIGHT_TEXTLOCATION_FAILED` / `BOOKMARK_TEXTLOCATION_FAILED` events.
     *
     * @fires IVanillaReaderAppEvent_HIGHLIGHT_ADDED
     * @fires IVanillaReaderAppEvent_HIGHLIGHT_TEXTLOCATION_FAILED
     *
     * */
    highlightTextLocation(
        textToHighlight: string,
        documentIndex: number,
        sourceHighlightData: IVanillaReaderHighlightData,
    ): void {
        this.vanillaReader.highlightTextLocation(
            textToHighlight,
            documentIndex,
            sourceHighlightData
        ).catch(console.warn);
    }

    /**
     *
     * This method helps a Screen Reader focus on the current reading position by calling `HTMLElement.focus()` as close
     * to the current reading position as possible.
     *
     * @fires IVanillaReaderAppEvent_FOCUS_ON_READINGPOSITION_DONE
     * @fires IVanillaReaderAppEvent_FOCUS_ON_READINGPOSITION_FAILED
     *
     * */
    focusOnReadingPosition(): void {
        this.vanillaReader.focusOnReadingPosition().catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_RENDERER_ASPECT_RATIO_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    ignorePublicationAspectRatio(ignoreAspectRatio: boolean): void {
        this.vanillaReader.ignorePublicationAspectRatio(ignoreAspectRatio);
    }

    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_LANDMARKS_READY
     * */
    fetchPublicationLandmarks(landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[]): Promise<void | IVanillaReaderPublicationLandmarkCollection> {
        return this.vanillaReader.fetchPublicationLandmarks(landmarkTypesToInclude).then((landmarkCollection: IVanillaReaderPublicationLandmarkCollection | undefined)=> {
            let headings = landmarkCollection?.headings || [];
            let footnotes = landmarkCollection?.footnotes || [];
            let figures = landmarkCollection?.figures || [];
            let tables = landmarkCollection?.tables || [];
            let lists = landmarkCollection?.lists || [];
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LANDMARKS_READY', {
                    headings,
                    footnotes,
                    figures,
                    tables,
                    lists,
                },
            ));
        });
    }

    /**
     * @fires IVanillaReaderAppEvent_DOCUMENT_LANDMARKS_READY
     * */
    fetchDocumentLandmarks(documentIndex: number, landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[]): Promise<IVanillaReaderDocumentLandmark> {
        return this.vanillaReader.fetchDocumentLandmarks(documentIndex, landmarkTypesToInclude);
    }

    /**
     * @fires IVanillaReaderAppEvent_NAVIGATION_STARTED
     * @fires IVanillaReaderAppEvent_NAVIGATION_COMPLETED
     * @fires IVanillaReaderAppEvent_READING_POSITION_UPDATED
     * @fires IVanillaReaderAppEvent_TIMELINE_POSITION_UPDATED
     * */
    navigate(navigationAction: IVanillaReaderNavigationAction): void {
        this.vanillaReader.navigate(navigationAction);
    }

    /**
     * @fires IVanillaReaderAppEvent_BOOKMARK_ADDED
     * */
    addBookmark(bookmarkLocator: string | ILocator): void {
        this.vanillaReader.addBookmark(bookmarkLocator);
    }

    /**
     * @fires IVanillaReaderAppEvent_BOOKMARK_DELETED
     * @fires IVanillaReaderAppEvent_BOOKMARKS_COLLECTION_UPDATED
     * */
    deleteBookmark(bookmarkLocator: string): void {
        this.vanillaReader.deleteBookmark(bookmarkLocator);
    }

    /**
     * @fires IVanillaReaderAppEvent_HIGHLIGHT_UPDATED
     * @fires IVanillaReaderAppEvent_HIGHLIGHTS_COLLECTION_UPDATED
     * */
    updateHighlight(highlightData: IVanillaReaderHighlightData): void {
        this.vanillaReader.updateHighlight(highlightData);
    }

    /**
     * @fires IVanillaReaderAppEvent_HIGHLIGHT_DELETED
     * @fires IVanillaReaderAppEvent_HIGHLIGHTS_COLLECTION_UPDATED
     * */
    deleteHighlight(highlightLocator: string): void {
        this.vanillaReader.deleteHighlight(highlightLocator);
    }

    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_OPTIONS_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setPublicationOptions(publicationOptions: IEpubReaderPublicationOptions | IPdfReaderPublicationOptions | IWpAudiobookReaderPublicationOptions): void {
        this.vanillaReader.setPublicationOptions(publicationOptions);
    }

    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_STYLE_OPTIONS_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setPublicationStyleOptions(styleOptions: IVanillaReaderPublicationStyleOptions): void {
        this.vanillaReader.setPublicationStyleOptions(styleOptions);
    }

    revokeMediaObjectUrl(mediaUrl?: string): void {
        this.vanillaReader.revokeMediaObjectUrl(mediaUrl);
    }

    fetchContentPositionDataForHref(href: string, baseDocumentLocator: string): Promise<IVanillaReaderContentPositionData | undefined> {
        return this.vanillaReader.fetchContentPositionDataForHref(href, baseDocumentLocator);
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_PAGE_MARKERS_VISIBILITY_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    toggleReaderViewPageMarkerVisibility(visible: boolean): void {
        this.vanillaReader.toggleReaderViewPageMarkerVisibility(visible);
    }

    /**
     * @fires IVanillaReaderAppEvent_REDUCED_MOTION_OPTION_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    useReducedMotion(useReducedMotion: boolean): void {
        this.vanillaReader.useReducedMotion(useReducedMotion);
    }

    /**
     * @fires IVanillaReaderAppEvent_RENDERER_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    changeRenderer(rendererName: VanillaReaderRendererNames, ignoreAspectRatio: boolean): void {
        this.vanillaReader.changeRenderer(rendererName, ignoreAspectRatio);
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_WILL_REFRESH
     * */
    refreshReaderView(force: boolean): void {
        this.vanillaReader.refreshReaderView(force);
    }

    clearContentSelection(): void {
        this.vanillaReader.clearContentSelection();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_CREATED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_CREATE_PROGRESS_UPDATED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_ATTACHED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_DETACHED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    toggleMediaPlayer(activate: boolean, options: IVanillaReaderSyncMediaOptions): void {
        this.vanillaReader.toggleMediaPlayer(activate, options).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaPlaybackNext(): void {
        this.vanillaReader.seekMediaPlaybackNext();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaPlaybackPrevious(): void {
        this.vanillaReader.seekMediaPlaybackPrevious();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaPlaybackForward(): void {
        this.vanillaReader.seekMediaPlaybackForward();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaPlaybackBackward(): void {
        this.vanillaReader.seekMediaPlaybackBackward();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaToPlaybackRangeStart(): void {
        this.vanillaReader.seekMediaToPlaybackRangeStart();
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaToLocator(locator: ILocator | ILocatorData): void {
        this.vanillaReader.seekMediaToLocator(locator);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_SEEK_COMPLETED
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED
     *
     * */
    seekMediaToLocatorWithinVisibleRange(locator: ILocator | ILocatorData): void {
        this.vanillaReader.seekMediaToLocatorWithinVisibleRange(locator);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_STATE_CHANGED
     * */
    startMediaPlayback(locator?: ILocatorData): void {
        this.vanillaReader.startMediaPlayback(locator).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_STATE_CHANGED
     * */
    pauseMediaPlayback(): void {
        this.vanillaReader.pauseMediaPlayback();
    }

    playTimelineRange(range: ISyncMediaTimelineRangeData, options?: ISyncMediaPlaybackRangeOptions): void {
        this.vanillaReader.vanillaMediaPlayer?.playTimelineRange(range, options);
    }

    /**
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setMediaPlaybackRate(rate: number): void {
        this.vanillaReader.setMediaPlaybackRate(rate);
    }

    /**
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setMediaPlaybackVolume(volume: number): void {
        this.vanillaReader.setMediaPlaybackVolume(volume);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_VOICE_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setTtsVoice(voiceName: string | undefined): void {
        this.vanillaReader.setTtsVoice(voiceName).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_MEDIAPLAYER_VOICE_LIST_FETCH_RESULT
     * */
    fetchTtsVoiceCollection(): Promise<ISpeechSynthesisVoiceCollection | undefined> {
        return this.vanillaReader.fetchTtsVoiceCollection();
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_PAN_MODE_ACTIVATED
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_CHANGED
     * */
    activatePanZoomMode(): void {
        this.vanillaReader.activatePanZoomMode();
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_RESET
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_PAN_MODE_DEACTIVATED
     * */
    deactivatePanZoomMode(): void {
        this.vanillaReader.deactivatePanZoomMode();
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_CHANGED
     * */
    zoomToClientRect(zoomRect: DOMRect, animate: boolean): Promise<IVanillaReaderTransformData | null> {
        return this.vanillaReader.zoomToClientRect(zoomRect, animate);
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_CHANGED
     * */
    zoomToPublicationViewportClientRect(zoomRect: DOMRect, unit: LengthUnit, transformOptions: ITransformRectZoomOptions): void {
        this.vanillaReader.zoomToPublicationViewportClientRect(zoomRect, unit, transformOptions);
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_RESET
     * */
    resetZoom(): void {
        this.vanillaReader.resetZoom();
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_TRANSFORM_CHANGED
     * */
    zoomToLevel(zoomLevel: number, animate: boolean): void {
        this.vanillaReader.zoomToLevel(zoomLevel, animate).catch(console.warn);
    }


    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_OPTIONS_CHANGED
     * */
    scrollOverflowingTables(scrollOverflowingTables: boolean): void {
        this.vanillaReader.scrollOverflowingTables(scrollOverflowingTables);
    }

    /**
     * @fires IVanillaReaderAppEvent_PUBLICATION_RESOURCES_DELETED
     * */
    deletePublicationResources(shelfItemData: IVanillaReaderShelfItemData): void {
        this._vanillaReaderAppMain.deletePublicationResources(shelfItemData).catch(console.warn);
    }

    /**
     * @fires IVanillaReaderAppEvent_VIEW_OPTIONS_CHANGED
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setViewOptions(viewOptions: IVanillaReaderViewOptions): void {
        this.vanillaReader.vanillaReaderView?.setVanillaReaderViewOptions(viewOptions);
    }

    playAmbientMedia(): void {
        if (this.vanillaReader.ambientMediaPlayer){
            this.vanillaReader.ambientMediaPlayer.play().catch(console.warn);
        }
    }

    stopAmbientMedia(): void {
        if (this.vanillaReader.ambientMediaPlayer){
            this.vanillaReader.ambientMediaPlayer.pause();
        }
    }

    /**
     * This method sets all UI related options across all modules and components in the Vanilla Reader app.
     * In the Colibrio Reader Framework there are many options that affect rendering. For example: font and text related
     * options, such as color values, line height and font style are options on the `IPublicationStyleOptions`; options
     * such as page aspect ratio, page turn animations belong to `IRendererOptions` etc.
     *
     * When calling this method, it will apply all these options. Very convenient.
     *
     * @param uiOptions - A *complete* set of options of type `IVanillaReaderUiOptionsData`. Missing options will be reset
     * to the defaults specified in the `VanillaReaderAppUiDefaults.options` object.
     *
     * @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     * */
    setAppUiOptions(uiOptions: IVanillaReaderUiOptionsData): void {

        if(!this._vanillaReader) {
            console.warn('VanillaReaderAPI.setAppUiOptions(), no VanillaReader instance has been referenced.');
            return;
        }

        let activePalette: IVanillaReaderUiColorTheme | undefined = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === uiOptions.activeUiPaletteName);
        const defaultPalette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Dark')!;
        const defaultOptions = VanillaReaderAppUiDefaults.options;

        if(!activePalette) {
            activePalette = defaultPalette;
        }

        uiOptions.useAccessibilityMode = uiOptions.useAccessibilityMode !== undefined ? uiOptions.useAccessibilityMode : defaultOptions.useAccessibilityMode;
        uiOptions.useDarkMode = uiOptions.useDarkMode !== undefined ? uiOptions.useDarkMode : defaultOptions.useDarkMode;
        uiOptions.activeRendererTypeName = uiOptions.activeRendererTypeName || VanillaReaderRendererNames.RESPONSIVE;
        uiOptions.ignoreAspectRatio = uiOptions.ignoreAspectRatio !== undefined ? uiOptions.ignoreAspectRatio : defaultOptions.ignoreAspectRatio!;
        uiOptions.useReducedMotion = uiOptions.useReducedMotion !== undefined ? uiOptions.useReducedMotion : defaultOptions.useReducedMotion!;
        uiOptions.scrollOverflowingTables = uiOptions.scrollOverflowingTables !== undefined ? uiOptions.scrollOverflowingTables : defaultOptions.scrollOverflowingTables!;
        uiOptions.mediaPlayerPlaybackRate = uiOptions.mediaPlayerPlaybackRate !== undefined ? uiOptions.mediaPlayerPlaybackRate : defaultOptions.mediaPlayerPlaybackRate!;
        uiOptions.readingRulerOptions = uiOptions.readingRulerOptions !== undefined ? uiOptions.readingRulerOptions : VanillaReaderAppUiDefaults.options.readingRulerOptions;
        uiOptions.showPageNumbers = uiOptions.showPageNumbers !== undefined ? uiOptions.showPageNumbers : defaultOptions.showPageNumbers!;
        uiOptions.showNarrationControls = uiOptions.showNarrationControls !== undefined ? uiOptions.showNarrationControls : defaultOptions.showNarrationControls;
        uiOptions.publicationStyleOptions = uiOptions.publicationStyleOptions !== undefined ? uiOptions.publicationStyleOptions : defaultOptions.publicationStyleOptions;
        uiOptions.ttsOptions = uiOptions.ttsOptions !== undefined ? uiOptions.ttsOptions : defaultOptions.ttsOptions;

        if(uiOptions.publicationStyleOptions) {
            this.setPublicationStyleOptions(uiOptions.publicationStyleOptions);
        }

        this.setViewOptions({
            activeRendererTypeName: uiOptions.activeRendererTypeName,
            ignoreAspectRatio: uiOptions.ignoreAspectRatio,
            useReducedMotion: uiOptions.useReducedMotion,
        });


        this.toggleReaderViewPageMarkerVisibility(uiOptions.showPageNumbers);
        this.scrollOverflowingTables(uiOptions.scrollOverflowingTables);
        this.setMediaPlaybackRate(uiOptions.mediaPlayerPlaybackRate);
        this.setTtsVoice(uiOptions.ttsOptions?.voiceName);

        this.setAppMainColorTheme(activePalette);
    }

    /**
     * Reset to the defaults specified in the `VanillaReaderAppUiDefaults.options` object.
     *
     *  @fires IVanillaReaderAppEvent_APP_UI_OPTIONS_CHANGED
     **/
    resetAppUiOptions(): void {
        let defaultPalette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Default')!;

        // Create a deep copy of the default options.
        const defaultOptionsString = JSON.stringify(VanillaReaderAppUiDefaults.options);
        const defaultOptions = JSON.parse(defaultOptionsString);

        defaultOptions.useAccessibilityMode = defaultOptions.useAccessibilityMode !== undefined ? defaultOptions.useAccessibilityMode : false;
        defaultOptions.useDarkMode = defaultOptions.useDarkMode !== undefined ? defaultOptions.useDarkMode : false;
        defaultOptions.activeRendererTypeName = defaultOptions.activeRendererTypeName || VanillaReaderRendererNames.RESPONSIVE;
        defaultOptions.ignoreAspectRatio = this.isMobileScreenSize ? true : (defaultOptions.ignoreAspectRatio !== undefined ? defaultOptions.ignoreAspectRatio : false);
        defaultOptions.useReducedMotion = defaultOptions.useReducedMotion !== undefined ? defaultOptions.useReducedMotion : false;
        defaultOptions.scrollOverflowingTables = defaultOptions.scrollOverflowingTables !== undefined ? defaultOptions.scrollOverflowingTables : false;
        defaultOptions.mediaPlayerPlaybackRate = defaultOptions.mediaPlayerPlaybackRate !== undefined ? defaultOptions.mediaPlayerPlaybackRate : 1;

        defaultOptions.readingRulerOptions = {
            rulerBackgroundColor: VanillaReaderAppUiDefaults.options.readingRulerOptions!.rulerBackgroundColor,
            overlayIsVisible: VanillaReaderAppUiDefaults.options.readingRulerOptions!.overlayIsVisible,
            lineBoxLineSpan: VanillaReaderAppUiDefaults.options.readingRulerOptions!.lineBoxLineSpan,
            snapToLineBoxes: VanillaReaderAppUiDefaults.options.readingRulerOptions!.snapToLineBoxes
        };

        defaultOptions.showPageNumbers = defaultOptions.showPageNumbers !== undefined ? defaultOptions.showPageNumbers : true;
        defaultOptions.showNarrationControls = defaultOptions.showNarrationControls !== undefined ? defaultOptions.showNarrationControls : false;

        // Finally we set the PublicationStyleOptions
        if (defaultOptions.publicationStyleOptions) {
            defaultOptions.publicationStyleOptions.palette = null;
            this.setPublicationStyleOptions(defaultOptions.publicationStyleOptions);
        }

        this.setViewOptions({
            activeRendererTypeName: defaultOptions.activeRendererTypeName,
            ignoreAspectRatio: defaultOptions.ignoreAspectRatio,
            useReducedMotion: defaultOptions.useReducedMotion,
        });

        this.toggleReaderViewPageMarkerVisibility(defaultOptions.showPageNumbers);
        this.scrollOverflowingTables(defaultOptions.scrollOverflowingTables);

        this.setMediaPlaybackRate(defaultOptions.mediaPlayerPlaybackRate);
        this.setTtsVoice(defaultOptions.ttsOptions?.voiceName);
        this.setAppMainColorTheme(defaultPalette);
    }

    /**
     *
     * Gets all the current options for the resulting `IVanillaReaderUiOptionsData` from the various modules and components
     * in the Vanilla Reader app.
     *
     * */
    private _collateVanillaReaderUiOptionsData(): IVanillaReaderUiOptionsData {
        const vanillaReaderViewOptions =  this.vanillaReader.vanillaReaderView?.getCurrentVanillaReaderViewOptionsState();
        const vanillaReaderPublicationStyleOptions = this._vanillaReader?.vanillaPublication?.publicationStyleOptions;
        const vanillaReaderSyncMediaOptions = this._vanillaReader?.vanillaMediaPlayer?.getPlaybackOptions();
        const vanillaReaderTtsOptions = this._vanillaReader?.vanillaMediaPlayer?.getTtsPlaybackOptions();

        let activeUiColorTheme: IVanillaReaderUiColorTheme | undefined;

        if(vanillaReaderPublicationStyleOptions?.palette) {
            activeUiColorTheme = this._getMatchingUiColorThemeByPublicationStylePalette(vanillaReaderPublicationStyleOptions.palette);
        }

        let options: IVanillaReaderUiOptionsData | undefined = {};
        const uiOptions = this._vanillaReaderAppMain.getVanillaReaderUiInstance()?.serializeToUiOptionsData() || {};

        options.readingRulerOptions = uiOptions.readingRulerOptions;
        options.useAccessibilityMode = uiOptions.useAccessibilityMode;
        options.showNarrationControls = uiOptions.showNarrationControls;
        options.showReadingRuler = uiOptions.showReadingRuler;
        options.ttsOptions = vanillaReaderTtsOptions || uiOptions.ttsOptions;
        options.scrollOverflowingTables = vanillaReaderViewOptions?.scrollOverflowingTables;
        options.ignoreAspectRatio = vanillaReaderViewOptions?.ignoreAspectRatio;
        options.showPageNumbers = vanillaReaderViewOptions?.showPageRangesLayer;
        options.activeRendererTypeName = vanillaReaderViewOptions?.activeRendererTypeName;
        options.useReducedMotion = vanillaReaderViewOptions?.useReducedMotion;
        options.publicationStyleOptions = vanillaReaderPublicationStyleOptions;
        options.mediaPlayerPlaybackRate = vanillaReaderSyncMediaOptions?.rate;
        options.activeUiPaletteName = activeUiColorTheme?.name;
        options.activePublicationFontSetName = vanillaReaderPublicationStyleOptions?.fontSet?.name;
        options.activePublicationPaletteName = activeUiColorTheme?.name;
        options.useDarkMode = activeUiColorTheme?.uiIsDark;

        return options;
    }

    private _getMatchingUiColorThemeByPublicationStylePalette(stylePalette: IVanillaReaderPublicationStylePalette): IVanillaReaderUiColorTheme | undefined {
        return VanillaReaderAppAssets.ColorThemes.find((palette) => {
            return (
                palette.accent === stylePalette.accent &&
                palette.backgroundDark === stylePalette.backgroundDark &&
                palette.backgroundLight === stylePalette.backgroundLight &&
                palette.foregroundDark === stylePalette.foregroundDark &&
                palette.foregroundLight === stylePalette.foregroundLight
            )
        });
    }

    /**
     *
     * EVENT CALLBACKS
     *
     * */

    onAmbientAudioReady(callback: VanillaReaderEventCallback<"AMBIENT_AUDIO_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('AMBIENT_AUDIO_READY', callback, once);
    }

    onAmbientAudioNotFound(callback: VanillaReaderEventCallback<"AMBIENT_AUDIO_NOT_FOUND">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('AMBIENT_AUDIO_NOT_FOUND', callback, once);
    }

    onAppLoaded(callback: VanillaReaderEventCallback<"APP_LOADED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_LOADED', callback, once);
    }

    onAppOnlineStateChanged(callback: VanillaReaderEventCallback<"APP_ONLINE_STATE_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_ONLINE_STATE_CHANGED', callback, once);
    }

    onBookmarkAdded(callback: VanillaReaderEventCallback<"BOOKMARK_ADDED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_ADDED', callback, once);
    }

    onBookmarkClicked(callback: VanillaReaderEventCallback<"BOOKMARK_CLICKED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_CLICKED', callback, once);
    }

    onBookmarkDeleted(callback: VanillaReaderEventCallback<"BOOKMARK_DELETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_DELETED', callback, once);
    }

    onBookmarkInVisibleRange(callback: VanillaReaderEventCallback<"BOOKMARK_IN_VISIBLE_RANGE">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_IN_VISIBLE_RANGE', callback, once);
    }

    onBookmarkOutsideVisibleRange(callback: VanillaReaderEventCallback<"BOOKMARK_OUTSIDE_VISIBLE_RANGE">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_OUTSIDE_VISIBLE_RANGE', callback, once);
    }

    onBookmarkTextLocationFailed(callback: VanillaReaderEventCallback<"BOOKMARK_TEXTLOCATION_FAILED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARK_TEXTLOCATION_FAILED', callback, once);
    }

    onBookmarksCollectionUpdated(callback: VanillaReaderEventCallback<"BOOKMARKS_COLLECTION_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARKS_COLLECTION_UPDATED', callback, once);
    }

    onBookmarksLoaded(callback: VanillaReaderEventCallback<"BOOKMARKS_LOADED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('BOOKMARKS_LOADED', callback, once);
    }

    onClick(callback: VanillaReaderEventCallback<"CLICK">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('CLICK', callback, once);
    }

    onDocumentIsAtEnd(callback: VanillaReaderEventCallback<"DOCUMENT_IS_AT_END">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('DOCUMENT_IS_AT_END', callback, once);
    }

    onDocumentIsAtStart(callback: VanillaReaderEventCallback<"DOCUMENT_IS_AT_START">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('DOCUMENT_IS_AT_START', callback, once);
    }

    onDocumentLandmarksReady(callback: VanillaReaderEventCallback<"DOCUMENT_LANDMARKS_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('DOCUMENT_LANDMARKS_READY', callback, once);
    }

    onHighlightAdded(callback: VanillaReaderEventCallback<"HIGHLIGHT_ADDED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_ADDED', callback, once);
    }

    onHighlightClicked(callback: VanillaReaderEventCallback<"HIGHLIGHT_CLICKED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_CLICKED', callback, once);
    }

    onHighlightDeleted(callback: VanillaReaderEventCallback<"HIGHLIGHT_DELETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_DELETED', callback, once);
    }

    onHighlightInVisibleRange(callback: VanillaReaderEventCallback<"HIGHLIGHT_IN_VISIBLE_RANGE">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_IN_VISIBLE_RANGE', callback, once);
    }

    onHighlightOutsideVisibleRange(callback: VanillaReaderEventCallback<"HIGHLIGHT_OUTSIDE_VISIBLE_RANGE">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_OUTSIDE_VISIBLE_RANGE', callback, once);
    }

    onHighlightTextLocationFailed(callback: VanillaReaderEventCallback<"HIGHLIGHT_TEXTLOCATION_FAILED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_TEXTLOCATION_FAILED', callback, once);
    }

    onHighlightUpdated(callback: VanillaReaderEventCallback<"HIGHLIGHT_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_UPDATED', callback, once);
    }

    onHighlightsCollectionUpdated(callback: VanillaReaderEventCallback<"HIGHLIGHTS_COLLECTION_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHTS_COLLECTION_UPDATED', callback, once);
    }

    onHighlightsLoaded(callback: VanillaReaderEventCallback<"HIGHLIGHTS_LOADED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('HIGHLIGHTS_LOADED', callback, once);
    }

    onMediaObjectViewIntent(callback: VanillaReaderEventCallback<"MEDIA_OBJECT_VIEW_INTENT">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIA_OBJECT_VIEW_INTENT', callback, once);
    }

    onMediaPlayerAttached(callback: VanillaReaderEventCallback<"MEDIAPLAYER_ATTACHED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_ATTACHED', callback, once);
    }

    onMediaPlayerCreated(callback: VanillaReaderEventCallback<"MEDIAPLAYER_CREATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_CREATED', callback, once);
    }

    onMediaPlayerDetached(callback: VanillaReaderEventCallback<"MEDIAPLAYER_DETACHED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_DETACHED', callback, once);
    }

    onMediaPlayerPlaybackPositionChanged(callback: VanillaReaderEventCallback<"MEDIAPLAYER_PLAYBACK_POSITION_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_PLAYBACK_POSITION_CHANGED', callback, once);
    }

    onMediaPlayerPlaybackStateChanged(callback: VanillaReaderEventCallback<"MEDIAPLAYER_PLAYBACK_STATE_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_PLAYBACK_STATE_CHANGED', callback, once);
    }

    onMediaPlayerSeekCompleted(callback: VanillaReaderEventCallback<"MEDIAPLAYER_SEEK_COMPLETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_SEEK_COMPLETED', callback, once);
    }

    onMediaPlayerPlaybackRangeEndReached(callback: VanillaReaderEventCallback<"MEDIAPLAYER_PLAYBACK_RANGE_END_REACHED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_PLAYBACK_RANGE_END_REACHED', callback, once);
    }

    onMediaPlayerPlaybackRangeRemoved(callback: VanillaReaderEventCallback<"MEDIAPLAYER_PLAYBACK_RANGE_REMOVED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_PLAYBACK_RANGE_REMOVED', callback, once);
    }

    onMediaPlayerVoiceChanged(callback: VanillaReaderEventCallback<"MEDIAPLAYER_VOICE_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_VOICE_CHANGED', callback, once);
    }

    onMediaPlayerEscapabilityContextChanged(callback: VanillaReaderEventCallback<"MEDIAPLAYER_ESCAPABILITY_CONTEXT_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_ESCAPABILITY_CONTEXT_CHANGED', callback, once);
    }

    onMediaObjectClicked(callback: VanillaReaderEventCallback<"MEDIA_OBJECT_CLICKED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIA_OBJECT_CLICKED', callback, once);
    }

    onMediaPlayerVoiceListChanged(callback: VanillaReaderEventCallback<"MEDIAPLAYER_VOICE_LIST_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('MEDIAPLAYER_VOICE_LIST_CHANGED', callback, once);
    }
    onNavigationCompleted(callback: VanillaReaderEventCallback<"NAVIGATION_COMPLETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('NAVIGATION_COMPLETED', callback, once);
    }

    onNavigationGotoInternalLocationIntent(callback: VanillaReaderEventCallback<"NAVIGATION_INTERNAL_INTENT">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('NAVIGATION_INTERNAL_INTENT', callback, once);
    }

    onNavigationOpenRemoteAddressIntent(callback: VanillaReaderEventCallback<"NAVIGATION_EXTERNAL_INTENT">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('NAVIGATION_EXTERNAL_INTENT', callback, once);
    }

    onNavigationStarted(callback: VanillaReaderEventCallback<"NAVIGATION_STARTED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('NAVIGATION_STARTED', callback, once);
    }

    onNavigationTreeReady(callback: VanillaReaderEventCallback<"NAVIGATION_TREE_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('NAVIGATION_TREE_READY', callback, once);
    }

    onPublicationDownloadProgressUpdated(callback: VanillaReaderEventCallback<"PUBLICATION_DOWNLOAD_PROGRESS_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_DOWNLOAD_PROGRESS_UPDATED', callback, once);
    }

    onPublicationIsAtStart(callback: VanillaReaderEventCallback<"PUBLICATION_IS_AT_START">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_IS_AT_START', callback, once);
    }

    onPublicationIsAtEnd(callback: VanillaReaderEventCallback<"PUBLICATION_IS_AT_END">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_IS_AT_END', callback, once);
    }

    onPublicationLandmarksReady(callback: VanillaReaderEventCallback<"PUBLICATION_LANDMARKS_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_LANDMARKS_READY', callback, once);
    }

    onPublicationLoadFailed(callback: VanillaReaderEventCallback<"PUBLICATION_LOAD_FAILED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_LOAD_FAILED', callback, once);
    }

    onPublicationLoaded(callback: VanillaReaderEventCallback<"PUBLICATION_LOADED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_LOADED', callback, once);
    }

    onPublicationPageListReady(callback: VanillaReaderEventCallback<"PUBLICATION_PAGE_LIST_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_PAGE_LIST_READY', callback, once);
    }

    onPublicationPageNumberVisibilityChanged(callback: VanillaReaderEventCallback<"VIEW_PAGE_MARKERS_VISIBILITY_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_PAGE_MARKERS_VISIBILITY_CHANGED', callback, once);
    }

    onPublicationRendered(callback: VanillaReaderEventCallback<"PUBLICATION_RENDERED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_RENDERED', callback, once);
    }

    onPublicationSearchCompleted(callback: VanillaReaderEventCallback<"PUBLICATION_SEARCH_COMPLETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_SEARCH_COMPLETED', callback, once);
    }

    onPublicationStyleOptionsChanged(callback: VanillaReaderEventCallback<"PUBLICATION_STYLE_OPTIONS_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_STYLE_OPTIONS_CHANGED', callback, once);
    }

    onPublicationViewportChanged(callback: VanillaReaderEventCallback<"PUBLICATION_VIEWPORT_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_VIEWPORT_CHANGED', callback, once);
    }

    onPublicationShelfUpdated(callback: VanillaReaderEventCallback<"PUBLICATION_SHELF_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_SHELF_UPDATED', callback, once);
    }

    onReaderKeyUp(callback: VanillaReaderEventCallback<"KEY_UP">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('KEY_UP', callback, once);
    }

    onReaderKeyDown(callback: VanillaReaderEventCallback<"KEY_DOWN">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('KEY_DOWN', callback, once);
    }

    onReaderViewPointerDown(callback: VanillaReaderEventCallback<"POINTER_DOWN">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('POINTER_DOWN', callback, once);
    }

    onReaderViewPointerMove(callback: VanillaReaderEventCallback<"POINTER_MOVE">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('POINTER_MOVE', callback, once);
    }

    onReaderViewPointerUp(callback: VanillaReaderEventCallback<"POINTER_UP">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('POINTER_UP', callback, once);
    }

    onReadingPositionUpdated(callback: VanillaReaderEventCallback<"READING_POSITION_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('READING_POSITION_UPDATED', callback, once);
    }

    onReducedMotionOptionChanged(callback: VanillaReaderEventCallback<"REDUCED_MOTION_OPTION_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('REDUCED_MOTION_OPTION_CHANGED', callback, once);
    }

    onRendererAspectRatioChanged(callback: VanillaReaderEventCallback<"RENDERER_ASPECT_RATIO_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('RENDERER_ASPECT_RATIO_CHANGED', callback, once);
    }

    onRendererChanged(callback: VanillaReaderEventCallback<"RENDERER_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('RENDERER_CHANGED', callback, once);
    }

    onTextSelected(callback: VanillaReaderEventCallback<"TEXT_SELECTED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('TEXT_SELECTED', callback, once);
    }

    onTimelinePositionUpdated(callback: VanillaReaderEventCallback<"TIMELINE_POSITION_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('TIMELINE_POSITION_UPDATED', callback, once);
    }

    onTimelineBuildProcessUpdated(callback: VanillaReaderEventCallback<"TIMELINE_BUILD_PROCESS_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('TIMELINE_BUILD_PROCESS_UPDATED', callback, once);
    }

    onTimelineReady(callback: VanillaReaderEventCallback<"TIMELINE_READY">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('TIMELINE_READY', callback, once);
    }

    onViewTransformChanged(callback: VanillaReaderEventCallback<"VIEW_TRANSFORM_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSFORM_CHANGED', callback, once);
    }

    onViewTransformStopped(callback: VanillaReaderEventCallback<"VIEW_TRANSFORM_STOPPED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSFORM_STOPPED', callback, once);
    }

    onViewTransitionEnded(callback: VanillaReaderEventCallback<"VIEW_TRANSITION_ENDED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSITION_ENDED', callback, once);
    }

    onViewTransitionStarted(callback: VanillaReaderEventCallback<"VIEW_TRANSITION_STARTED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSITION_STARTED', callback, once);
    }

    onVisibleContentRectsChanged(callback: VanillaReaderEventCallback<"VISIBLE_CONTENT_RECTS_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VISIBLE_CONTENT_RECTS_CHANGED', callback, once);
    }

    onVisibleContentRendered(callback: VanillaReaderEventCallback<"VISIBLE_CONTENT_RENDERED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VISIBLE_CONTENT_RENDERED', callback, once);
    }

    onVisiblePagesChanged(callback: VanillaReaderEventCallback<"VISIBLE_PAGES_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VISIBLE_PAGES_CHANGED', callback, once);
    }

    onVisibleRangeDataChanged(callback: VanillaReaderEventCallback<"VISIBLE_RANGE_DATA_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VISIBLE_RANGE_DATA_CHANGED', callback, once);
    }

    onPublicationSearchCleared(callback: VanillaReaderEventCallback<"PUBLICATION_SEARCH_CLEARED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_SEARCH_CLEARED', callback, once);
    }

    onPublicationStoredResourcesDeleted(callback: VanillaReaderEventCallback<"PUBLICATION_STORED_RESOURCES_DELETED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_STORED_RESOURCES_DELETED', callback, once);
    }

    onViewPanZoomModeActivated(callback: VanillaReaderEventCallback<"VIEW_TRANSFORM_PAN_MODE_ACTIVATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSFORM_PAN_MODE_ACTIVATED', callback, once);
    }

    onViewPanZoomModeDeactivated(callback: VanillaReaderEventCallback<"VIEW_TRANSFORM_PAN_MODE_DEACTIVATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_TRANSFORM_PAN_MODE_DEACTIVATED', callback, once);
    }

    onViewSwipeNavigationProgressUpdated(callback: VanillaReaderEventCallback<"VIEW_SWIPE_NAVIGATION_PROGRESS_UPDATED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_SWIPE_NAVIGATION_PROGRESS_UPDATED', callback, once);
    }

    onAppWindowResized(callback: VanillaReaderEventCallback<"APP_WINDOW_RESIZED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_WINDOW_RESIZED', callback, once);
    }

    onAppUiOptionsReset(callback: VanillaReaderEventCallback<"APP_UI_OPTIONS_RESET">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_UI_OPTIONS_RESET', callback, once);
    }

    onAppUiOptionsChanged(callback: VanillaReaderEventCallback<"APP_UI_OPTIONS_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_UI_OPTIONS_CHANGED', callback, once);
    }

    onAppUiOptionsPresetApplied(callback: VanillaReaderEventCallback<"APP_UI_OPTIONS_PRESET_APPLIED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_UI_OPTIONS_PRESET_APPLIED', callback, once);
    }

    onAppUiThemeChanged(callback: VanillaReaderEventCallback<"APP_UI_THEME_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('APP_UI_THEME_CHANGED', callback, once);
    }

    onPublicationClose(callback: VanillaReaderEventCallback<"PUBLICATION_CLOSE_INTENT">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('PUBLICATION_CLOSE_INTENT', callback, once);

    }

    onViewOptionsChanged(callback: VanillaReaderEventCallback<"VIEW_OPTIONS_CHANGED">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_OPTIONS_CHANGED', callback, once);
    }

    onViewWillRefresh(callback: VanillaReaderEventCallback<"VIEW_WILL_REFRESH">, once?: boolean): void {
        VanillaReaderEventBus.addEventListener('VIEW_WILL_REFRESH', callback, once);
    }

}
